// Wave 21 — admin health dashboard.
//
// One-stop view aggregating per-provider CAPI delivery, postback
// delivery status, retry-queue + DLQ depth, FX cache freshness,
// and cost-sync connection state. Backend does all the bucketing
// in `admin_health.py`; this page just renders the envelope.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

const WINDOWS = [
  { label: '1h', seconds: 3600 },
  { label: '6h', seconds: 6 * 3600 },
  { label: '24h', seconds: 86400 },
  { label: '7d', seconds: 7 * 86400 },
];

function formatRate(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${rate.toFixed(2)}%`;
}

function rateTone(rate) {
  if (rate === null || rate === undefined) return 'muted';
  if (rate >= 99) return 'good';
  if (rate >= 95) return 'warn';
  return 'bad';
}

function ToneBadge({ tone, children }) {
  const map = {
    good: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    warn: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    bad: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    muted: 'bg-surface-2 text-foreground border-border-2',
  };
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${map[tone] || map.muted}`}
    >
      {children}
    </span>
  );
}

export default function AdminHealth() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowSeconds, setWindowSeconds] = useState(86400);

  const fetchHealth = async (windowSec = windowSeconds) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/health`, {
        params: { window_seconds: windowSec },
      });
      setData(r.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail || t('Failed to load admin health'),
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth(windowSeconds);
  }, [windowSeconds]); // eslint-disable-line

  return (
    <div className="px-8 pt-2 pb-8 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{t('System health')}</CardTitle>
            <CardDescription>
              {t(
                'Aggregated CAPI / postback / FX / cost-sync state across the workspaces you administer.',
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-border overflow-hidden">
              {WINDOWS.map((w) => (
                <button
                  key={w.seconds}
                  type="button"
                  onClick={() => setWindowSeconds(w.seconds)}
                  className={`px-3 py-1 text-xs ${
                    windowSeconds === w.seconds
                      ? 'bg-surface-2 text-foreground'
                      : 'bg-transparent text-muted-foreground hover:bg-surface-2/50'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => fetchHealth()}
              disabled={loading}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              {t('Refresh')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {error && (
        <Card>
          <CardContent className="py-4 text-rose-400">{error}</CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* CAPI delivery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('CAPI delivery')}</span>
                <ToneBadge tone={rateTone(data.capi_events?.success_rate)}>
                  {formatRate(data.capi_events?.success_rate)}
                </ToneBadge>
              </CardTitle>
              <CardDescription>
                {t('Conversion API dispatches in the last window.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Provider')}</TableHead>
                    <TableHead>{t('Total')}</TableHead>
                    <TableHead>{t('Success')}</TableHead>
                    <TableHead>{t('Failed')}</TableHead>
                    <TableHead>{t('Success rate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.keys(data.capi_events?.by_provider || {}).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground">
                        {t('No CAPI events in window.')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.entries(data.capi_events.by_provider).map(
                      ([provider, stats]) => (
                        <TableRow key={provider}>
                          <TableCell className="font-mono">{provider}</TableCell>
                          <TableCell>{stats.total}</TableCell>
                          <TableCell>{stats.success}</TableCell>
                          <TableCell>{stats.failed}</TableCell>
                          <TableCell>
                            <ToneBadge tone={rateTone(stats.success_rate)}>
                              {formatRate(stats.success_rate)}
                            </ToneBadge>
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Retry queue + DLQ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('Retry queue')}</CardTitle>
                <CardDescription>
                  {t('Pending CAPI re-attempts.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  {t('Total')}:{' '}
                  <span className="font-semibold">
                    {data.capi_retry_queue?.total ?? 0}
                  </span>
                </div>
                <div>
                  {t('Due now')}:{' '}
                  <span className="font-semibold">
                    {data.capi_retry_queue?.due_now ?? 0}
                  </span>
                </div>
                <div>
                  {t('Oldest')}:{' '}
                  <span className="text-foreground">
                    {data.capi_retry_queue?.oldest_age_human || '—'}
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {t('Dead-letter queue')}
                  {(data.capi_dead_letter?.total || 0) > 0 && (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  )}
                </CardTitle>
                <CardDescription>
                  {t('Conversions that exhausted the retry budget.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  {t('Total')}:{' '}
                  <span className="font-semibold">
                    {data.capi_dead_letter?.total ?? 0}
                  </span>
                </div>
                <div>
                  {t('Oldest')}:{' '}
                  <span className="text-foreground">
                    {data.capi_dead_letter?.oldest_age_human || '—'}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {Object.entries(data.capi_dead_letter?.by_provider || {}).map(
                    ([p, n]) => (
                      <span key={p} className="mr-3">
                        {p}: <strong>{n}</strong>
                      </span>
                    ),
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Postback delivery */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('Postback delivery')}</span>
                <ToneBadge tone={rateTone(data.postback_events?.success_rate)}>
                  {formatRate(data.postback_events?.success_rate)}
                </ToneBadge>
              </CardTitle>
              <CardDescription>
                {t('S2S postback callouts in the last window.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                {[
                  ['ok_2xx', 'good'],
                  ['client_4xx', 'warn'],
                  ['server_5xx', 'bad'],
                  ['no_response', 'bad'],
                  ['other', 'muted'],
                ].map(([bucket, tone]) => (
                  <div
                    key={bucket}
                    className="rounded-md border border-border bg-surface/40 p-3"
                  >
                    <div className="text-xs text-muted-foreground mb-1">{bucket}</div>
                    <div className="text-2xl font-semibold tabular-nums">
                      {data.postback_events?.buckets?.[bucket] ?? 0}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* FX cache */}
          <Card>
            <CardHeader>
              <CardTitle>{t('FX cache')}</CardTitle>
              <CardDescription>
                {t('Per-base exchange-rate snapshot freshness.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Base')}</TableHead>
                    <TableHead>{t('Refreshed')}</TableHead>
                    <TableHead>{t('Status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.fx_cache?.bases || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground">
                        {t('FX cache is empty.')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.fx_cache.bases.map((b) => (
                      <TableRow key={b.base}>
                        <TableCell className="font-mono">{b.base}</TableCell>
                        <TableCell>{b.refreshed_age_human}</TableCell>
                        <TableCell>
                          {b.is_stale ? (
                            <Badge variant="destructive">{t('stale')}</Badge>
                          ) : (
                            <Badge variant="outline">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {t('fresh')}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Cost-sync */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('Ad cost sync')}</span>
                <ToneBadge
                  tone={
                    data.cost_sync?.error_count > 0
                      ? 'bad'
                      : data.cost_sync?.total_active > 0
                        ? 'good'
                        : 'muted'
                  }
                >
                  {data.cost_sync?.ok_count ?? 0}/
                  {data.cost_sync?.total_active ?? 0}
                </ToneBadge>
              </CardTitle>
              <CardDescription>
                {t('Active connections, last sync time, and recent errors.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Provider')}</TableHead>
                    <TableHead>{t('Account')}</TableHead>
                    <TableHead>{t('Last sync')}</TableHead>
                    <TableHead>{t('Status')}</TableHead>
                    <TableHead>{t('Last error')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data.cost_sync?.connections || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground">
                        {t('No active ad-cost connections.')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.cost_sync.connections.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono">
                          {c.provider}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.ad_account_id || '—'}
                        </TableCell>
                        <TableCell>{c.last_synced_age_human}</TableCell>
                        <TableCell>
                          {c.has_error ? (
                            <Badge variant="destructive">{t('error')}</Badge>
                          ) : (
                            <Badge variant="outline">{t('ok')}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-rose-300 max-w-md truncate">
                          {c.last_error || '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="text-xs text-muted-foreground text-center">
            {t('Generated at')}:{' '}
            {data.generated_at ? new Date(data.generated_at).toLocaleString() : '—'}
            {' · '}
            {t('Window')}: {data.window_seconds}s
          </div>
        </>
      )}
    </div>
  );
}
